/**
 * Client team console (/team/*) for org managers (Enterprise teams,
 * 2026-10-03). Reuses the Staff Console panels with the API base switched
 * to /api/team, which the leads worker scopes to the manager's own org,
 * plus the team-only panels in team-panels.tsx (approvals, booked calls,
 * account health, ownership, compliance export). No staff tab is reachable.
 */

import { NavLink, Navigate, Route, Routes, Link } from 'react-router-dom'
import {
  Activity,
  ArrowLeft,
  CalendarCheck,
  ClipboardCheck,
  Download,
  FileText,
  HeartPulse,
  Loader2,
  MessageSquare,
  Send,
  ShieldCheck,
  Sparkles,
  Users,
  UsersRound,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { AdminApiBaseContext } from '@/features/admin/admin-base'
import { CampaignsPage, ContentPage, InboxPage, LeadsPage, UsersPage } from '@/features/admin/super-admin'
import { ApprovalsPage, BookingsPage, ExportPage, HealthPage, OwnershipPage, TeamOverviewPage } from './team-panels'
import { useTeamMe } from './use-team'

export const TEAM_TABS = [
  { to: '/team', label: 'Overview', icon: Activity, end: true },
  { to: '/team/reps', label: 'Reps', icon: Users },
  { to: '/team/leads', label: 'Leads', icon: Sparkles },
  { to: '/team/campaigns', label: 'Campaigns', icon: Send },
  { to: '/team/content', label: 'Content', icon: FileText },
  { to: '/team/inbox', label: 'Conversations', icon: MessageSquare },
  { to: '/team/approvals', label: 'Approvals', icon: ClipboardCheck },
  { to: '/team/calls', label: 'Booked calls', icon: CalendarCheck },
  { to: '/team/health', label: 'Account health', icon: HeartPulse },
  { to: '/team/ownership', label: 'Ownership', icon: ShieldCheck },
  { to: '/team/export', label: 'Compliance export', icon: Download },
] as const

function TeamNav() {
  return (
    <div className="flex flex-nowrap items-center gap-1 overflow-x-auto border-b border-border pb-3 [scrollbar-width:none] sm:flex-wrap [&::-webkit-scrollbar]:hidden">
      {TEAM_TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={'end' in t ? t.end : false}
          className={({ isActive }) =>
            cn(
              'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-[rgba(28,63,255,0.12)] text-[var(--color-glow-white)]'
                : 'text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-silver-bright)]',
            )
          }
        >
          <t.icon className="h-3.5 w-3.5" />
          {t.label}
        </NavLink>
      ))}
    </div>
  )
}

function NotManager() {
  return (
    <div className="flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
          <UsersRound className="h-10 w-10 text-[var(--color-silver)]" />
          <h2 className="text-xl font-semibold text-[var(--color-silver-bright)]">Team managers only</h2>
          <p className="text-sm text-[var(--color-silver)]">
            The team view is for managers of a DealCloser team account. If you should have access, ask your account
            manager or support@dealcloserautopilot.com.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-2">
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to dashboard
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export function TeamApp() {
  const { data: me, isLoading } = useTeamMe()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    )
  }
  if (!me?.is_manager) return <NotManager />

  return (
    <AdminApiBaseContext.Provider value="/api/team">
      <div className="mx-auto max-w-7xl space-y-5">
        <div>
          <p className="eyebrow text-[var(--color-cobalt-bright)]">Team</p>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--color-silver-bright)]">{me.org?.name ?? 'Your team'}</h1>
          <p className="mt-1 text-sm text-[var(--color-silver)]">
            Every rep&apos;s leads, campaigns, content and conversations in one place. You can approve held messages or
            hand them back to the rep; everything else is read only.
          </p>
        </div>
        <TeamNav />
        <Routes>
          <Route index element={<TeamOverviewPage />} />
          <Route path="reps" element={<UsersPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="content" element={<ContentPage />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="calls" element={<BookingsPage />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="ownership" element={<OwnershipPage />} />
          <Route path="export" element={<ExportPage />} />
          <Route path="*" element={<Navigate to="/team" replace />} />
        </Routes>
      </div>
    </AdminApiBaseContext.Provider>
  )
}
